import { useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";
import API from "../api";

// ADMIN → TRAITORS — flip each promo weekend's availability and review
// registrations of interest. Saving rewrites the weekend list the public
// Traitors Island page reads.

export default function TraitorsManager() {
  const [weekends, setWeekends] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = () => {
    fetch(`${API}/api/traitors-weekends`)
      .then((r) => r.json())
      .then((d) => setWeekends(d.weekends || []))
      .catch(() => toast.error("Could not load weekends"));
    fetch(`${API}/api/admin/traitors-enquiries`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => setRegistrations(d.registrations || []))
      .catch(() => {});
  };

  useEffect(load, []);

  if (!weekends) {
    return <p className="mt-10 text-center font-display text-2xl italic text-ivory/50">Loading Traitors Island settings…</p>;
  }

  const toggle = (id) => {
    setWeekends((ws) => ws.map((w) => (w.id === id ? { ...w, available: !w.available } : w)));
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/admin/traitors-weekends`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ weekends }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Save failed");
      setWeekends(data.weekends);
      toast.success("Weekend availability updated — live on the site straight away.");
    } catch (e) {
      toast.error(String(e.message || e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="pb-20">
      <p className="mt-6 max-w-3xl text-sm font-light leading-relaxed text-ivory/70">
        Toggle weekend availability below and save — the Traitors Island page updates instantly. Registrations of
        interest appear underneath.
      </p>

      <div className="mt-8 grid max-w-3xl gap-4" data-testid="traitors-weekend-toggles">
        {weekends.map((w) => (
          <div key={w.id} className="flex items-center justify-between gap-4 border border-ivory/20 bg-navy p-5">
            <div>
              <p className="font-display text-lg text-ivory" data-testid={`traitors-weekend-title-${w.id}`}>
                {w.label}
              </p>
              <p className="mt-1 text-xs font-light text-ivory/50">Balance deadline: {w.balance_deadline}</p>
            </div>
            <button
              type="button"
              onClick={() => toggle(w.id)}
              data-testid={`traitors-availability-toggle-${w.id}`}
              className={`px-5 py-2.5 text-[11px] font-semibold uppercase tracking-widest2 transition-colors ${w.available ? "bg-green-800 text-ivory" : "bg-red-900/60 text-ivory/80"}`}
            >
              {w.available ? "Available" : "Fully booked"}
            </button>
          </div>
        ))}
      </div>
      <button
        onClick={save}
        disabled={saving}
        data-testid="traitors-save-button"
        className="mt-6 bg-gold px-6 py-3 text-[11px] font-semibold uppercase tracking-widest2 text-navy transition-colors hover:bg-gold-2 disabled:opacity-50"
      >
        {saving ? "Saving…" : "Save availability"}
      </button>

      <h3 className="mt-12 font-display text-2xl text-ivory">Registrations of interest</h3>
      <p className="mt-2 text-xs uppercase tracking-widest2 text-coastal" data-testid="traitors-registration-count">
        {registrations.length} registration{registrations.length === 1 ? "" : "s"} · newest first
      </p>
      <div className="mt-6 flex flex-col gap-4" data-testid="traitors-registrations-list">
        {registrations.length === 0 && (
          <p className="mt-10 text-center font-display text-2xl italic text-ivory/50" data-testid="traitors-empty">
            No registrations yet — they will appear here the moment someone registers.
          </p>
        )}
        {registrations.map((r) => (
          <div key={r.id} className="border border-ivory/20 bg-navy p-6" data-testid="traitors-registration-row">
            <div className="flex flex-wrap items-baseline justify-between gap-3">
              <p className="font-display text-xl text-ivory">{r.full_name}</p>
              <p className="text-xs font-light text-ivory/50">
                {String(r.created_at || "").slice(0, 10)} · {r.group_size} guests
              </p>
            </div>
            <p className="mt-2 text-sm text-gold">{r.weekend_label}</p>
            <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm font-light text-ivory/75">
              <a href={`mailto:${r.email}`} className="underline decoration-gold/50 hover:text-gold-2">{r.email}</a>
              <a href={`tel:${r.phone}`} className="underline decoration-gold/50 hover:text-gold-2">{r.phone}</a>
            </div>
            {r.message && <p className="mt-3 text-sm font-light leading-relaxed text-ivory/70">“{r.message}”</p>}
            <a
              href={`https://wa.me/${String(r.phone).replace(/[^0-9]/g, "")}`}
              target="_blank" rel="noopener noreferrer"
              className="mt-4 inline-block border border-gold/50 px-4 py-2 text-[10px] font-semibold uppercase tracking-widest2 text-gold hover:bg-gold hover:text-navy"
            >
              WhatsApp them
            </a>
          </div>
        ))}
      </div>
      <button
        onClick={load}
        data-testid="traitors-refresh-button"
        className="mt-8 flex items-center gap-2 border border-ivory/25 px-4 py-2 text-[11px] font-semibold uppercase tracking-widest2 text-ivory/80 transition-colors hover:border-gold hover:text-gold-2"
      >
        <RefreshCw className="h-3.5 w-3.5" /> Refresh
      </button>
    </div>
  );
}
